import React from 'react';
import SideNavItem from './sidenav/components/SideNavItem';
import AddonsSideNavItem from './sidenav/components/AddonsSideNavItem';
import SideNavContent from './sidenav/components/SideNavContent';
import Notifications from '../factory/notification/Notifications';
import { flowRight as compose } from 'lodash';
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import Auth from '../Auth';
import { Notification } from '../factory/notification/model';

export interface FactorySideNavProps {
  path: string;
  notifications: Notification[];
}

const FactorySideNav: React.FC<FactorySideNavProps> = (props) => {
  const { path, notifications } = props;
  const isAdmin = Auth.isAdmin();
  const canAccessPricing = Auth.canAccessPricing();
  const canManageWarehouses = Auth.canManageWarehouses();
  const canManageRepairs = Auth.canManageRepairs();
  const badgeLength = notifications ? notifications.length : null;

  return (
    <div>
      <ul className="navigation navigation--light">
        <SideNavItem
          submenuIcon="Bell"
          submenuLabel="notifications.title"
          icon="Bell"
          badge={badgeLength && badgeLength > 9 ? '9+' : badgeLength}
          label="notifications.title"
        >
          <Notifications />
        </SideNavItem>
        <AddonsSideNavItem />
      </ul>
      <SideNavContent path={path}>
        <SideNavItem link="/dashboard" icon="Dashboard" label="dashboard.title" />
        <SideNavItem link="/schemas" icon="Schemas" label="schemas.title" />
        <SideNavItem link="/reports" icon="Reports" label="reports.title" />
        <SideNavItem link="/graphs" icon="Graphs" label="graphs.title" />
        <SideNavItem link="/events" icon="Parameters" label="factory.events" />
        {canAccessPricing && <SideNavItem link="/prices" icon="Prices" label="prices" />}
        {canManageWarehouses && (
          <SideNavItem link="/warehouses" icon="Warehouses" label="warehouses.title" />
        )}
        {canManageRepairs && (
          <>
            <SideNavItem link="/equipmentObjects" icon="Equipments" label="equipments.title" />
            <SideNavItem link="/repairObjects" icon="Repairs" label="repairs.title" />
          </>
        )}

        <SideNavItem link="/journals/issue" icon="Events" label="issues.title" />
        <SideNavItem link="/journals/basic" icon="Journal" label="journalNames.title" />
        {isAdmin && (
          <SideNavItem submenuIcon="Cog" submenuLabel="administration" label="administration" link="/settings" icon="Cog">
            <ul className="navigation">
              <SideNavItem link="/settings/objects" label="objects.title" />
              <SideNavItem link="/settings/areas" label="areas.title" />
              <SideNavItem link="/settings/equipments" label="equipments.title" />
              <SideNavItem link="/settings/indicators" label="indicators.title" />
              <SideNavItem link="/settings/unknown-indicators" label="unknownIndicators.title" />
              <SideNavItem link="/settings/units" label="units.title" />
              <SideNavItem link="/settings/journal-names" label="journalNames.title" />
              <SideNavItem link="/settings/journal-templates" label="journalTemplates.title" />
              <SideNavItem link="/settings/issue-types" label="issueTypes.title" />
              <SideNavItem link="/settings/issue-states" label="issueStates.title" />
              <SideNavItem link="/settings/users" label="users" />
            </ul>
          </SideNavItem>
        )}
      </SideNavContent>
    </div>
  );
};

const NOTIFICATIONS_QUERY = gql`
  query Notifications {
    notifications {
      id
      message
      url
      createdAt
    }
  }
`;

const withData = (Component: React.FC<FactorySideNavProps>) => {
  return (props: Omit<FactorySideNavProps, 'notifications'>) => {
    const { data } = useQuery<{ notifications: Notification[] }>(NOTIFICATIONS_QUERY, {
      fetchPolicy: 'network-only',
      pollInterval: 60000,
    });

    return (
      <Component
        {...(props as FactorySideNavProps)}
        notifications={data?.notifications || []}
      />
    );
  };
};

export default compose(withData)(FactorySideNav);
